import React, { useEffect, useState } from 'react';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import {
  object,
  pageTitle,
  location,
  UT30N9J,
  productTitle,
  pageUrl,
} from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ButtonSet,
  ContentSwitcher,
  ExpandableTile,
  SkeletonPlaceholder,
  Slider,
  Switch,
  TileAboveTheFoldContent,
  TileBelowTheFoldContent,
  Tooltip,
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  InlineNotification,
} from 'carbon-components-react';
import './GuardiumInsightsPriceCalculator.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import messages from '../locales/messages';
import { FormattedMessage } from 'react-intl';
import Thanks from './Thanks';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { useLang } from 'src/common/hooks/widgets/useLang';

import {
  AWS_SALES,
  MVS,
  SAAS,
  SOFTWARE,
  Tiles,
  Users,
  checkForChina,
} from '../constants/default';
import { FormModal } from 'src/common/utils/FormModal';
import { shortcode } from '../GuardiumInsightsPriceCalculator.widget';
import countryList from '../services/currencies.json';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';
import { segmentsTracking } from '../lib/tracker';
import { ArrowRight16, Launch16 } from '@carbon/icons-react';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import OmniChannel from 'src/common/components/OmniChannel/OmniChannel';
import { getParamsFromUrl } from 'src/common/utils/getParamsFromUrl';

export interface GuardiumInsightsPriceCalculatorProps {
  langCode: string;
  countryToTest: string;
  test: boolean;
}

const GuardiumInsightsPriceCalculator = ({
  langCode,
  countryToTest,
  test,
}: GuardiumInsightsPriceCalculatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [omnichannel, setOmniChannel] = useState(
    getParamsFromUrl('widgetForm') === 'true',
  );
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);

  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  const cookieURL = useCookiesURL();

  // lang and userlocale is based out of ddo.
  // lang and userlocale variable will be use once we start introduce multi-language.
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const { bpName, logo, companyId, desc } = useBPName(UT30N9J, true);

  // Related to asset
  const [tileValueSelected, setTileValueSelected] = useState(Tiles.dspm);
  const [mvs, setMVS] = useState(MVS.default);
  const [users, setUsers] = useState(Users.default);
  const [solution, setSolution] = useState(
    checkForChina(country) ? SOFTWARE : SAAS,
  );
  const [essentials, setEssentials] = useState(false);
  const [premium, setPremium] = useState(false);
  const [standard, setStandard] = useState(false);
  const [dspm, setDspm] = useState(true);
  const [
    totalCost,
    dsPrices,
    totalStoragePrice,
    totalDspmUsersPrice,
    storageQty,
    loading,
  ] = usePrice({
    currency,
    country,
    localeCode,
    mvs,
    solution,
    tileValueSelected,
    users,
  });
  const [formattedStorageQty] = useNumberFormat(Number(storageQty));

  useEffect(() => {
    // On pre-prod and prod widget will have the parent div with shortcode classname.
    // So we are applying margin because some AEM classname causing style issue.
    const shortCodeStyle = Array.from(
      document.getElementsByClassName(
        shortcode,
      ) as HTMLCollectionOf<HTMLElement>,
    )[0];
    // In local shortcode will be alway undefined, to avoid app crash on local we are
    // checking the below condition and it will only work in pre-prod and prod version.
    if (shortCodeStyle !== undefined) {
      shortCodeStyle.style.marginLeft = '1rem';
    }
  }, []);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  useEffect(() => {
    setCookie();
  }, [mvs, totalCost]);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Offering Selected: ' +
            solution +
            ' | Package: ' +
            tileValueSelected +
            ' Total MVS: ' +
            mvs +
            ' Total Cost for MVS: ' +
            dsPrices +
            ' Total Storage in GB: ' +
            storageQty +
            ' Total Cost for the storage: ' +
            totalStoragePrice +
            ' | Estimated one time cost: ' +
            totalCost,
          Q_ASSET_URL: window.location.href, // ISC - Asset URL
          Q_ASSET_NAME: 'IBM Gurardium Insights Price Calculator', // ISC - Asset Name
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  /**
   * Handlers to check max and min and set value
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    if (value >= min && value <= max) {
      setValue(value);
    } else if (value > max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  const checkForSaaS = () => {
    return solution === SAAS && !checkForChina(country);
  };

  const changeExpandedTiles = (
    expandedTilesStats: boolean,
    setExpandedTiles: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    setExpandedTiles(!expandedTilesStats);
  };

  return (
    <div className="guardium-insights widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          mvs={mvs}
          oneTimeFee={`${totalCost}`}
          packageName={tileValueSelected}
          bpName={bpName}
        />
      ) : (
        <div id="GuardiumInsightsGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4">
                  <div className="page_sub_heading marginleft_1">
                    <FormattedMessage {...messages.gi_main_headline} />
                  </div>
                </div>
              </div>
              <div className="bx--row mar3">
                <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4">
                  <p className="page_body marginleft_1">
                    <FormattedMessage {...messages.gi_sub_headline} />
                  </p>
                </div>
              </div>

              <div className="bx--row mar3">
                <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4">
                  {country === 'CHN' ? (
                    <ContentSwitcher
                      className="content-switcher"
                      selectedIndex={0}
                      size="md"
                      onChange={(obj) => {
                        const { name } = obj;

                        setSolution(name + '');
                        setMVS(MVS.default);
                        setUsers(Users.default);
                        setTileValueSelected(Tiles.dspm);
                        setEssentials(false);
                        setDspm(true);
                        setStandard(false);
                        setPremium(false);
                      }}
                    >
                      <Switch name={SOFTWARE} text="Software" />
                    </ContentSwitcher>
                  ) : (
                    <ContentSwitcher
                      className="content-switcher"
                      selectedIndex={0}
                      size="md"
                      onChange={(obj) => {
                        const { name } = obj;
                        setSolution(name + '');
                        setMVS(MVS.default);
                        setUsers(Users.default);
                        setTileValueSelected(Tiles.dspm);
                        setEssentials(false);
                        setDspm(true);
                        setStandard(false);
                        setPremium(false);
                      }}
                    >
                      <Switch name={SAAS} text="SaaS" />
                      <Switch name={SOFTWARE} text="Software" />
                    </ContentSwitcher>
                  )}
                </div>
              </div>
              <div className="bx--row mar3">
                {checkForSaaS() ? (
                  <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-4">
                    <ExpandableTile
                      id="essentials"
                      tileCollapsedIconText="Interact to Expand tile"
                      tileExpandedIconText="Interact to Collapse tile"
                      expanded={essentials}
                      onClick={() => {
                        changeExpandedTiles(essentials, setEssentials);
                        setTileValueSelected(Tiles.essentials);
                        if (!essentials) {
                          setStandard(false);
                          setDspm(false);
                          setPremium(false);
                        }
                        segmentsTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'essentials tiles clicked',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <TileAboveTheFoldContent>
                        <div className="page_sub_heading">
                          <FormattedMessage {...messages.est_label} />
                        </div>
                      </TileAboveTheFoldContent>
                      <TileBelowTheFoldContent>
                        <div className="page_body_2 mar8">
                          <FormattedMessage {...messages.est_tile_desc} />
                        </div>
                      </TileBelowTheFoldContent>
                    </ExpandableTile>
                    <ExpandableTile
                      id="standard"
                      tileCollapsedIconText="Interact to Expand tile"
                      tileExpandedIconText="Interact to Collapse tile"
                      expanded={standard}
                      onClick={() => {
                        changeExpandedTiles(standard, setStandard);
                        setTileValueSelected(Tiles.standard);
                        if (!standard) {
                          setEssentials(false);
                          setDspm(false);
                          setPremium(false);
                        }
                        segmentsTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'standard tiles clicked',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <TileAboveTheFoldContent>
                        <div className="page_sub_heading">
                          <FormattedMessage {...messages.std_label} />
                        </div>
                      </TileAboveTheFoldContent>
                      <TileBelowTheFoldContent>
                        <div className="page_body_2 mar8">
                          <FormattedMessage {...messages.std_tile_desc} />
                        </div>
                      </TileBelowTheFoldContent>
                    </ExpandableTile>
                    <ExpandableTile
                      id="premium"
                      tileCollapsedIconText="Interact to Expand tile"
                      tileExpandedIconText="Interact to Collapse tile"
                      expanded={premium}
                      onClick={() => {
                        changeExpandedTiles(premium, setPremium);
                        setTileValueSelected(Tiles.premium);
                        if (!premium) {
                          setStandard(false);
                          setDspm(false);
                          setEssentials(false);
                        }
                        segmentsTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'premium tiles clicked',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <TileAboveTheFoldContent>
                        <div className="page_sub_heading">
                          <FormattedMessage {...messages.prem_label} />
                        </div>
                      </TileAboveTheFoldContent>
                      <TileBelowTheFoldContent>
                        <div className="page_body_2 mar8">
                          <FormattedMessage {...messages.prem_tile_desc} />
                        </div>
                      </TileBelowTheFoldContent>
                    </ExpandableTile>
                    <ExpandableTile
                      id="dspm"
                      tileCollapsedIconText="Interact to Expand tile"
                      tileExpandedIconText="Interact to Collapse tile"
                      expanded={dspm}
                      onClick={() => {
                        changeExpandedTiles(dspm, setDspm);
                        setTileValueSelected(Tiles.dspm);
                        if (!dspm) {
                          setStandard(false);
                          setPremium(false);
                          setEssentials(false);
                        }
                        segmentsTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'dspm tiles clicked',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <TileAboveTheFoldContent>
                        <div className="page_sub_heading">
                          <FormattedMessage {...messages.dspm_label} />
                        </div>
                      </TileAboveTheFoldContent>
                      <TileBelowTheFoldContent>
                        <div className="page_body_2 mar8">
                          <FormattedMessage {...messages.dspm_tile_desc} />
                        </div>
                      </TileBelowTheFoldContent>
                    </ExpandableTile>
                  </div>
                ) : null}
                <div className="bx--col-lg-12 bx--col-md-8 bx--col-sm-4">
                  <div>
                    <div className="bx--row">
                      {checkForSaaS() ? (
                        <div className="bx--col-lg-1 bx--col-md-8 bx--col-sm-4"></div>
                      ) : null}

                      <div className="bx--col-lg-15 bx--col-md-8 bx--col-sm-4 marginleft_1">
                        <div
                          id="widget.estimated-cost"
                          className="guardium-insights__ibm-type-e estmt-cost price_table_comp__bodycopy"
                        >
                          {tileValueSelected !== Tiles.premium && (
                            <div>
                              <div className="bx--col-lg-10 bx--col-md-8 bx--col-sm-4 zero-padding-left">
                                <InlineNotification
                                  kind="info"
                                  role="onprem"
                                  title={
                                    checkForSaaS() ? (
                                      <FormattedMessage
                                        {...messages.inline_saas}
                                      />
                                    ) : (
                                      <FormattedMessage
                                        {...messages.inline_sw}
                                      />
                                    )
                                  }
                                  subtitle={
                                    checkForSaaS() ? (
                                      <FormattedMessage
                                        {...messages.inline_saas_description}
                                      />
                                    ) : (
                                      <FormattedMessage
                                        {...messages.inline_sw_description}
                                      />
                                    )
                                  }
                                  statusIconDescription="Premium Information"
                                  hideCloseButton={true}
                                />
                              </div>
                              <label className="page_body">
                                <FormattedMessage
                                  {...messages.gi_slider1_text}
                                />
                              </label>
                              <Tooltip
                                onChange={() => {
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: pageTitle,
                                    object: object,
                                    resultValue: 'CTA clicked',
                                    CTA: 'GI MVS tooltip',
                                    location: location,
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="slide_text">
                                  <FormattedMessage
                                    {...messages.tooltip_slider1_text}
                                  />
                                </span>
                              </Tooltip>
                              <Slider
                                id="mvs"
                                min={MVS.min}
                                max={MVS.max}
                                inputType={'number'}
                                value={mvs}
                                invalid={false}
                                labelText={''}
                                onBlur={() => {
                                  null;
                                }}
                                onChange={({ value }) => {
                                  handleSliders(
                                    value,
                                    setMVS,
                                    MVS.max,
                                    MVS.min,
                                  );
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: pageTitle,
                                    object: object,
                                    resultValue: 'CTA clicked',
                                    CTA: 'GI MVS slider',
                                    location: location,
                                    action: '',
                                    field: '',
                                  });
                                }}
                              />
                            </div>
                          )}
                          {checkForSaaS() &&
                            tileValueSelected === Tiles.dspm && (
                              <div>
                                <label className="page_body">
                                  <FormattedMessage
                                    {...messages.gi_slider2_text}
                                  />
                                </label>
                                <Tooltip
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: pageTitle,
                                      object: object,
                                      resultValue: 'CTA clicked',
                                      CTA: 'GI DSPM total users tooltip',
                                      location: location,
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  direction={isSmall ? 'bottom' : 'right'}
                                  tabIndex={0}
                                >
                                  <span className="slide_text">
                                    <FormattedMessage
                                      {...messages.tooltip_slider2_text}
                                    />
                                  </span>
                                </Tooltip>
                                <Slider
                                  id="users"
                                  min={Users.min}
                                  max={Users.max}
                                  inputType={'number'}
                                  value={users}
                                  invalid={false}
                                  labelText={''}
                                  onBlur={() => {
                                    null;
                                  }}
                                  onChange={({ value }) => {
                                    handleSliders(
                                      value,
                                      setUsers,
                                      Users.max,
                                      Users.min,
                                    );
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: pageTitle,
                                      object: object,
                                      resultValue: 'CTA clicked',
                                      CTA: 'GI DSPM users slider',
                                      location: location,
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                />
                              </div>
                            )}
                          {tileValueSelected !== Tiles.premium && (
                            <div className="bx--row mar3 pricing-horizontal-gaps">
                              {/* Items descriptions */}
                              {checkForSaaS() && (
                                <div className="bx--col-lg-9 bx--col-md-7 bx--col-sm-4">
                                  <Table className="widget-table">
                                    <TableHead>
                                      <TableRow>
                                        <TableHeader>
                                          {' '}
                                          <FormattedMessage
                                            {...messages.col1_text}
                                          />
                                        </TableHeader>
                                        <TableHeader>
                                          <FormattedMessage
                                            {...messages.col2_text}
                                          />
                                        </TableHeader>
                                        <TableHeader>
                                          <FormattedMessage
                                            {...messages.col3_text}
                                          />
                                        </TableHeader>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell>
                                          {loading ? (
                                            <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton-sm" />
                                          ) : (
                                            `${mvs}`
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {loading ? (
                                            <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton-sm" />
                                          ) : (
                                            <FormattedMessage
                                              {...messages.summary_text1}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>{dsPrices}</TableCell>
                                      </TableRow>
                                      {tileValueSelected !== Tiles.dspm ? (
                                        <TableRow>
                                          <TableCell>
                                            {loading ? (
                                              <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton-sm" />
                                            ) : (
                                              `${formattedStorageQty} GB`
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {' '}
                                            <FormattedMessage
                                              {...messages.summary_text2}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {loading ? (
                                              <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton" />
                                            ) : (
                                              `${totalStoragePrice} `
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        <TableRow>
                                          <TableCell>
                                            {loading ? (
                                              <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton-sm" />
                                            ) : (
                                              `${users} `
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {' '}
                                            <FormattedMessage
                                              {...messages.summary_text3}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {loading ? (
                                              <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton-sm" />
                                            ) : (
                                              `${totalDspmUsersPrice} `
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                              {/* Pricing Table */}
                              <div className="bx--col-lg-7 bx--col-md-7 bx--col-sm-4">
                                <Table className="price-table">
                                  <TableHead>
                                    <TableRow>
                                      <TableHeader>
                                        {' '}
                                        {checkForSaaS() ? (
                                          <FormattedMessage
                                            {...messages.estimated_annual_text}
                                          />
                                        ) : (
                                          <FormattedMessage
                                            {...messages.estimated_one_time_cost_text}
                                          />
                                        )}
                                        <sup className="guardium-insights__sup">
                                          1
                                        </sup>
                                      </TableHeader>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell className="noborder-bottom">
                                        <div className="padding_8 price_table_comp__price_lg_bold">
                                          {loading ? (
                                            <SkeletonPlaceholder className="guardium-insights__sticky-nav-skeleton" />
                                          ) : (
                                            `${totalCost} *`
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell>
                                        <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                                          *
                                          <FormattedMessage
                                            {...messages.prcie_text}
                                          />
                                        </p>
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                          {tileValueSelected === Tiles.premium ? (
                            <div className="bx--row">
                              <div className="bx--col-lg-10 bx--col-md-8 bx--col-sm-4">
                                <InlineNotification
                                  kind="info"
                                  role="onprem"
                                  title={
                                    <FormattedMessage
                                      {...messages.prem_label}
                                    />
                                  }
                                  subtitle={
                                    <FormattedMessage
                                      {...messages.contact_info}
                                    />
                                  }
                                  statusIconDescription="Premium Information"
                                  hideCloseButton={true}
                                />

                                <ButtonSet
                                  className="guardium-insights__stack-button mt-08"
                                  stacked
                                >
                                  <Button
                                    id="premium_btn"
                                    target="_target"
                                    data-ibm-contact="scheduler-link"
                                    href="#"
                                    onClick={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: pageTitle,
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'book a consultation',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      window.open(
                                        ' https://www.ibm.com/products/guardium-insights/pricing/?schedulerform=',
                                        '_target',
                                      );
                                    }}
                                  >
                                    <FormattedMessage {...messages.book_text} />
                                  </Button>
                                </ButtonSet>
                              </div>
                            </div>
                          ) : (
                            <div className="bx--row mar3">
                              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                                <ButtonSet className="btn-horizontal-gaps">
                                  {tileValueSelected !== Tiles.premium &&
                                    tileValueSelected !== Tiles.essentials &&
                                    tileValueSelected !== Tiles.standard && (
                                      <>
                                        <Button
                                          className="guardium-insights__bluehighlight"
                                          renderIcon={ArrowRight16}
                                          id="request_btn"
                                          onClick={() => {
                                            segmentsTracking('CTA Clicked', {
                                              pageTitle: pageTitle,
                                              object: 'Pricing page',
                                              resultValue: 'CTA clicked',
                                              CTA: 'Request a quote now',
                                              location: 'Estimator',
                                              action: '',
                                              field: '',
                                            });
                                            initUrxForm(
                                              environment,
                                              URX_FORM.INSTANCE_ID,
                                              URX_FORM.FORM_ID,
                                              urxlang as IBMLocale,
                                              companyId,
                                            );
                                            setDrawerOpen(true);
                                            setCookie();
                                          }}
                                        >
                                          <FormattedMessage
                                            {...messages.quote_text}
                                          />
                                        </Button>
                                      </>
                                    )}
                                  {tileValueSelected === Tiles.essentials &&
                                    solution === SAAS && (
                                      <OmniChannel
                                        id="guardium-insights-price-calculator"
                                        omniChannel={omnichannel}
                                        setUrxForm={setDrawerOpen}
                                        setOmniChannel={setOmniChannel}
                                        environment={environment}
                                        instanceId={URX_FORM.INSTANCE_ID}
                                        formId={URX_FORM.FORM_ID}
                                        urxlang={urxlang as IBMLocale}
                                        pageTitle={pageTitle}
                                        object={object}
                                        location={location}
                                        tiles={tileValueSelected}
                                        setCookie={setCookie}
                                        widgetTitle="Guardium Insights"
                                      />
                                    )}
                                  {tileValueSelected === Tiles.standard &&
                                    solution === SAAS && (
                                      <>
                                        <Button
                                          className="guardium-insights__bluehighlight"
                                          renderIcon={ArrowRight16}
                                          id="request_btn"
                                          onClick={() => {
                                            segmentsTracking('CTA Clicked', {
                                              pageTitle: pageTitle,
                                              object: 'Pricing page',
                                              resultValue: 'CTA clicked',
                                              CTA: 'Request a quote now',
                                              location: 'Estimator',
                                              action: '',
                                              field: '',
                                            });
                                            initUrxForm(
                                              environment,
                                              URX_FORM.INSTANCE_ID,
                                              URX_FORM.FORM_ID,
                                              urxlang as IBMLocale,
                                              companyId,
                                            );
                                            setDrawerOpen(true);
                                            setCookie();
                                          }}
                                        >
                                          <FormattedMessage
                                            {...messages.quote_text}
                                          />
                                        </Button>
                                        <Button
                                          renderIcon={Launch16}
                                          id="aws_btn"
                                          href={AWS_SALES}
                                          target="_blank"
                                          rel="noreferrer"
                                          kind="tertiary"
                                          onClick={() => {
                                            segmentsTracking('CTA Clicked', {
                                              pageTitle: pageTitle,
                                              object: 'Pricing page',
                                              resultValue: 'CTA clicked',
                                              CTA: 'aws sales button clicked',
                                              location: 'Estimator',
                                              action: '',
                                              field: '',
                                            });
                                            setCookie();
                                          }}
                                        >
                                          <FormattedMessage
                                            {...messages.contact_sales}
                                          />
                                        </Button>
                                      </>
                                    )}
                                </ButtonSet>
                                <br />
                                <div id="footnote">
                                  <div>
                                    <Footnote numNote={1} />
                                  </div>
                                  <div className="footnote_text">
                                    <DisclaimerText
                                      localeCode={localeCode}
                                      product="Guardium Insights"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle={pageTitle}
            object={object}
            productTitle={productTitle}
            pageUrl={pageUrl}
            location={location}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={URX_FORM.FORM_ID}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default GuardiumInsightsPriceCalculator;
